(ns arthur.domain.node
  "A node is an instance in the scene: what kind of mark it is, who it hangs off,
  what clips it, where it sits in draw order, and a bag of channels.

  The tree is stored FLAT, WITH PARENT POINTERS, never as nested maps. Four
  reasons that all point the same way: any node is addressable without a walk;
  reparenting is a one-field write rather than a subtree move; an edit to a leaf
  does not change the identity of its ancestors, so re-frame's structural sharing
  keeps ancestor subs from invalidating; and it is what lets every node be its own
  sync leaf. Flash, Blender and After Effects all store it this way.

  Transforms are DECOMPOSED for storage and FLAT AND MUTABLE for evaluation, and
  the two forms are allowed to differ. Decomposed because each component has to
  be independently keyframable — that is what channels are for — and because
  interpolating matrix entries is meaningless: a rotation tweened through its
  matrix shears on the way. Flat Float64Array for evaluation because at 30fps
  per-frame allocation is the only thing that will make this stutter."
  (:require [arthur.domain.channel :as ch]
            [clojure.string :as str]))

(def kinds
  "`:bitmap` is in the vocabulary and not implemented; it is
  here so that a scene that names one fails as \"not implemented\" rather than as
  \"not a kind\"."
  #{:poly :disc :rect :group :bitmap :instance :audio})

(def implemented-kinds #{:poly :disc :rect :group :instance :audio})

(def xform-paths
  "In composition order, which is also the order they have to be sampled in.

  :skew is in here although nothing drives it yet. A decomposition is not
  extensible after the fact: adding a component later means migrating every
  stored transform, so it is in the shape and in the composition order from the
  start.

  :pivot IS THE POINT ROTATION AND SCALE HAPPEN ABOUT, in the node's own
  coordinates, and it is in the decomposition because THERE IS NOWHERE ELSE IT
  CAN BE. Toon Boom gives every layer and peg a pivot; Flash gives every instance
  a transformation point; After Effects calls it the anchor point. All three store
  it, and all three are right to, for one reason: a turn has to be a turn ON EVERY
  FRAME, and the only way to keep a point still through an interpolated angle is
  for the angle to be composed about that point. Solving for the `pos` that holds
  a point still — `gesture/about` — is exact on the frame it is solved and wrong
  between two keys, because the solution is an ARC in the angle and `pos` tweens
  along the CHORD. See docs/animation-model.md, which has the measurements off the
  document where this was found.

  So `local!` is `T(pos)·T(piv)·R·K·S·T(-piv)`, the transform conjugated by a
  translation — \"do M in a frame shifted by piv\" — and the node's pivot point
  sits at `pos + piv` in its parent. A pivot of `[0 0]`, which is the default,
  leaves that exactly `T(pos)·R·K·S`: a drawing needs no pivot, because
  `paint/centred` already put its origin on the middle of what it draws.

  A PEG IS STILL A PEG. `nest/peg` is how a pivot gets SHARED between nodes, or
  put above a measured transform that a regenerate would overwrite; this is how
  one node turns about its own middle. The two were conflated — the peg was made
  to stand in for the pivot — and that is what cost a keyed turn its pivot."
  [[:xform :pos] [:xform :pivot] [:xform :rot] [:xform :scale] [:xform :skew]])

(def valid-paths
  "The set of valid channel paths follows from the node's :kind, and that is a
  SPEC rather than a schema migration — a node does not grow or lose fields, it
  simply has no `[:geom :radius]` unless it is a disc.

  Written out per kind rather than derived from a table shared with the
  renderer. What a kind may CARRY and what the renderer READS off it coincide
  today and are not the same question, and tying them together would make a
  change to this spec silently change what gets drawn."
  (let [base (into #{[:vis]} xform-paths)]
    {:group base
     ;; A palette-track placement is still an instance. Its palette choice is
     ;; a parameter channel on that instance, so the generic span commands can
     ;; move and trim it without knowing what kind of lane owns it.
     :instance (conj base [:palette])
     ;; A sound is not in the picture: no transform, no visibility. After
     ;; Effects' audio-only layer has no Transform group for the same reason.
     :audio #{[:audio :gain] [:audio :pan] [:audio :rate]}
     :poly  (into base [[:geom :pts] [:style :color]])
     ;; A disc's radius is framed in practice — iris size is a knob, not a
     ;; performance — but it is a channel like any other so it can be keyed.
     :disc  (into base [[:geom :radius] [:style :color]])
     ;; :size, not a radius: the pupil is a SQUARE, an exactly size x size
     ;; block. See raster/fill-rect!.
     :rect  (into base [[:geom :size] [:style :color]])}))

(def defaults
  "The identity transform, as channels. A node's channel map is merged over this,
  so a hand-written scene says only what it means to say.

  THE DEFAULT PIVOT IS THE NODE'S OWN ORIGIN, which is a real default and not a
  missing value: a drawing's origin IS the middle of what it draws — `paint/centred`
  put it there — so a shape turns about its own middle with no pivot stored, and
  `local!` collapses to `T(pos)·R·K·S` for it. What needs a pivot of its own is a
  node whose content is nowhere near its origin: a symbol instance, whose origin
  is the symbol's, and a measured part, whose origin is the corner of the footage.
  Both get one where they are made — `clip/place-symbol` — and `gesture/turn`
  gives one to anything that turns without having been given one."
  {[:xform :pos]    (ch/framed [0.0 0.0])
   [:xform :pivot]  (ch/framed [0.0 0.0])
   [:xform :rot]    (ch/framed 0.0)
   [:xform :scale]  (ch/framed [1.0 1.0])
   [:xform :skew]   (ch/framed [0.0 0.0])
   [:vis]           (ch/framed true)})

(def audio-defaults
  "Unity gain, centred, at its own speed."
  {[:audio :gain] (ch/framed 1.0)
   [:audio :pan]  (ch/framed 0.0)
   [:audio :rate] (ch/framed 1.0)})

(def ^:private channel-names
  {[:xform :pos] "pos" [:xform :rot] "rot°" [:xform :scale] "scale"
   [:xform :skew] "skew" [:xform :pivot] "pivot" [:geom :pts] "shape"
   [:style :color] "color" [:vis] "vis" [:palette] "palette"})

(defn channel-name
  "What to call channel `path` on screen: the same short word in the inspector
  and the timeline."
  [path]
  (or (channel-names path) (str/join " " (map name path))))

(defn defaults-of [n]
  (if (= :audio (:kind n)) audio-defaults defaults))

(defn channels
  "The node's channels with its kind's defaults filled in."
  [n]
  (merge (defaults-of n) (:channels n)))

(defn measured?
  "Is this node's transform regenerated from the footage rather than authored?

  What it guards is that a hand edit to a measured transform is thrown away by
  the next regenerate, which is what `gesture/refusal` refuses. The way to
  transform one of these by hand is a PEG above it: the hand transform is then on
  a node of its own and the measured channels underneath are left to be
  regenerated. Nothing has to be written onto the measured node at all.

  ITS PIVOT IS STILL ITS OWN, and that is the one part of its transform a hand
  may write: `[:xform :pivot]` is authored on every node alike, never dense and
  never regenerated, so a measured mouth can be told to turn about its own middle
  without a peg and without anything a regenerate would discard. What the peg is
  still for is the TRANSFORM over a measured one, which is this function's
  business; where the pivot goes is not."
  [n]
  (boolean (some #(let [c (get-in n [:channels [:xform %]])]
                    (or (:dense c) (:generated c)))
                 [:pos :rot :scale])))

(defn hold-only?
  "Channels whose values are choices, not quantities.  Their keys may change at
  a frame boundary but there is no meaningful value between two keys."
  [path value]
  (or (= path [:style :color])
      (boolean? value)
      (and (keyword? value) (not= path [:palette]))))

(defn- enforce-hold [path c]
  (if (and (= path [:style :color]) (:keys c))
    (-> c (assoc :interp :hold) (dissoc :segments))
    c))

(defn set-channel
  "Write `v` into channel `path`: a key on the node's own frame `f` when the
  channel is keyed, its one value when it is not."
  [n path f v]
  (let [c (get (channels n) path)]
    (assoc-in n [:channels path]
              (enforce-hold
               path
               (if (:keys c)
                 (assoc-in c [:keys f] v)
                 (merge (select-keys c [:semantic]) (ch/framed v)))))))

(defn set-keyed-channel
  "Write `v` as a key at `f`, starting an animated channel when needed. This is
  the auto-key counterpart to `set-channel`; an existing channel keeps its
  interpolation and segment choices."
  [n path f v]
  (let [c (get (channels n) path)]
    (assoc-in n [:channels path]
              (enforce-hold
               path
               (if (:keys c)
                 (assoc-in c [:keys f] v)
                 (merge (select-keys c [:semantic])
                        (ch/keyed {f v} (if (hold-only? path v) :hold :linear))))))))

(defn toggle-key
  "Key channel `path` on the node's own frame `f` with the value it has there, or
  take the key there off. The first key starts the channel animating and taking
  the last one off leaves it that one value. A boolean holds; anything else tweens.

  `store` because the value it keys is read out of the channel, and a measured
  channel's values live in tier 2.  Colour is always held even though current
  documents store palette choices as numeric slot indices."
  [n path f store]
  (let [c  (get (channels n) path)
        v  (ch/value-at c f store)
        ks (dissoc (:keys c) f)]
    (assoc-in n [:channels path]
              (enforce-hold
               path
               (cond
                 (not (:keys c))               (merge (select-keys c [:semantic])
                                                       (ch/keyed {f v} (if (hold-only? path v) :hold :linear)))
                 (not (contains? (:keys c) f)) (assoc-in c [:keys f] v)
                 (seq ks)                      (cond-> (assoc c :keys ks)
                                                 (:segments c) (update :segments dissoc f))
                 :else                         (ch/framed v))))))

(defn key-at
  "Key `n` on its own frame `f` with the values it has there: every channel
  already keyed, or its position, turn and scale when none is — Blender's I.
  A channel with a key at `f` already keeps it."
  [n f store]
  (let [chs (channels n)
        keyed (keep (fn [[path c]] (when (:keys c) path)) chs)]
    (reduce (fn [n path]
              (if (contains? (:keys (get (channels n) path)) f) n (toggle-key n path f store)))
            n
            (or (seq keyed)
                (filter #(contains? chs %) [[:xform :pos] [:xform :rot] [:xform :scale]])))))

(defn set-segment-interp
  "Choose how channel `path`'s key at `left` leads to the next one: `:hold` cuts
  there, `:linear` tweens. The same for a drawing's points as for a transform —
  only a gap that exists, between a key and a later one, can be chosen."
  [n path left interp]
  (let [ks (:keys (get (channels n) path))]
    (if (and (contains? ks left) (some #(< left %) (keys ks))
             (#{:hold :linear} interp)
             (or (= :hold interp) (not (hold-only? path nil))))
      (assoc-in n [:channels path :segments left] interp)
      n)))

;; ---------------------------------------------------------------------------
;; time maps
;;
;; Cel, mouth lead and a symbol instance's timing are ONE mechanism, and
;; seeing that is what keeps them from being three implementations that disagree
;; at the edges.

(defn expose
  "Hold a frame back onto an exposure grid: 1 = on 1s, 2 = on 2s. Frame 5 at
  cel 2 reads the pose from frame 4.

  FLOOR, NEVER ROUND. Rounding would let an output frame read a pose from the
  FUTURE, which is a lead — a separate control, applied after this one, for a
  separate reason."
  [f n]
  (if (and n (> n 1)) (* (js/Math.floor (/ f n)) n) f))

(def same-time
  "The identity time map: these frames ARE those frames. What a walk starts from
  before it has composed anything, and what `time-of` gives a node with no time
  of its own."
  {:at 0 :rate 1})

(defn time-of
  "A node's own time as the affine map it is: `{:at a :rate r}`, meaning a frame
  `p` of its parent is frame `r·(p − a)` of its own. THE SAME FOR EVERY NODE. A
  node with no time map is `{:at 0 :rate 1}`, reading its parent's frames as its
  own; a mouth lead's `:offset` is folded into `:at`. Exposure is a floor, not
  part of the map, and is left out: this is the map a move preserves and a
  timeline row draws with, and `local-frame` is what reads a frame, the floor and
  the lead in their load-bearing order.

  `:rate` is a RETIME SOMEBODY CHOSE and nothing else — half speed on an insert.
  Footage at another rate than the document's is played by the `:playback
  :speed` of its placement, not by this."
  [n]
  (let [{:keys [mode at rate offset] :or {mode :map at 0 rate 1 offset 0}} (:time n)]
    (if (= mode :map)
      {:at (- at (/ offset rate)) :rate rate}
      {:at 0 :rate 1})))

(defn mapped-time?
  "Whether `n`'s own time is the affine map `time-of` reads, and therefore
  whether moving it is a write to `:time :at`.

  AN ABSENT `:mode` IS `:map`, which is what `time-of` has always defaulted it
  to — and the default is the ordinary case, not an edge one: `span/held` writes
  `{:at f :rate 1}` with no mode at all, so every clip a drawing creates is in
  it. Code that asked `(= :map (get-in n [:time :mode]))` instead answered no
  for those, and `nest/slide` acted on the answer by REPLACING the whole map
  with a fresh one — so the first drag of a freshly drawn clip discarded its
  `:at` and teleported it to the head of the lane."
  [n]
  (= :map (:mode (:time n) :map)))

(defn then-time
  "`outer` then `inner`: the map from `outer`'s parent straight to `inner`'s own
  frames. Time maps compose like matrices do, which is what makes a nesting of
  any depth one map."
  [{a1 :at r1 :rate} {a2 :at r2 :rate}]
  {:at (+ a1 (/ a2 r1)) :rate (* r1 r2)})

(defn invert-time [{:keys [at rate]}]
  {:at (- (* at rate)) :rate (/ 1 rate)})

(defn placed-span
  "Where a node exists, as `[in out)` in its PARENT's frames, or nil for always.

  A `:span` is in the node's OWN frames — which of its frames exist — for every
  node alike, and its time map says where they land in the parent. For a node
  with no time map the two are the same frames, so a shape's span reads as it
  always did. Moving a node along its parent is then one write to `:at`, and the
  span, which says what the node IS, does not change when it is moved."
  [n]
  (when-let [[in out] (:span n)]
    (let [{:keys [at rate]} (time-of n)]
      [(+ at (/ in rate)) (+ at (/ out rate))])))

;; ---------------------------------------------------------------------------
;; what an instance places
;;
(defn source
  "The symbol used by this cel. Sequence groups arrange cels;
  a row is a view of that group, not one row per source."
  [n]
  (when (= :instance (:kind n)) (get-in n [:source :symbol])))

(defn sources
  "Structural references, including cels outside the playhead."
  [n]
  (if-let [sid (source n)] #{sid} #{}))

(defn playback-of [n]
  (merge {:in 0 :speed 1 :end :stop} (:playback n)))

(defn source-time
  "Invertible cel -> source map, or nil for holds and endpoint policies.
  Forward sampling remains available through `placed-frame` in every case."
  [n]
  (let [{:keys [in speed end]} (playback-of n)]
    (when (and (pos? speed) (= :stop end) (not (get-in n [:time :loop?])))
      {:at (- (/ in speed)) :rate speed})))

(defn placed-frame
  "Sample source time without changing the cel's property clock:
  `{:symbol :frame}`, the symbol shown and which of its frames. `length` is that
  symbol's frame count. Nil means no source contribution — this cel
  places nothing, or its playback has run past what there is to show."
  [n f length]
  (when-let [sid (source n)]
    (when (and (number? length) (pos? length))
      (let [{:keys [in speed end]} (playback-of n)
            raw (+ in (* speed f))
            frame (case (if (get-in n [:time :loop?]) :loop end)
                    :loop (mod raw length)
                    :hold (max 0 (min (dec length) raw))
                    :stop raw)]
        (when (and (<= 0 frame) (< frame length))
          {:symbol sid :frame frame})))))

(defn finite-number? [v] (and (number? v) (js/Number.isFinite v)))

(defn hold
  "Floor `f` onto the last of `holds` at or before it, and before the first onto
  the first. Exposure on authored frames rather than on a grid: `[0 12 30]` shows
  frame 12 from 12 until 30. What a tracing layer's held photos and a face's
  trace keys are.

  A `reduce` that stops at the first hold past `f`, because this runs per node
  per frame and the list is sorted."
  [f holds]
  (if (seq holds)
    (reduce (fn [held h] (if (<= h f) h (reduced held))) (first holds) holds)
    f))

(defn local-frame
  "Apply an artistic time map within one frame space: expose, hold, then offset.
  Frame-rate selection happens at symbol boundaries in domain/clip.

  Holds come after exposure and inherit the same way, strictly: they are a floor
  of this node's own frame, and its children are handed the floored frame."
  [n f]
  (let [{:keys [mode at rate offset holds] ex :expose :or {mode :map at 0 rate 1}} (:time n)]
    (if (= mode :inherit)
      f
      (cond-> (* rate (- f at))
        ex           (expose ex)
        (seq holds)  (hold holds)
        offset       (+ offset)))))

;; ---------------------------------------------------------------------------
;; the transform
;;
;; A 2x3 affine as a 6-element Float64Array [a b c d e f], the canvas convention:
;;
;;     | a c e |        x' = a·x + c·y + e
;;     | b d f |        y' = b·x + d·y + f
;;     | 0 0 1 |

(defn mat [] (js/Float64Array. #js [1 0 0 1 0 0]))

(defn set-identity! [^js m]
  (aset m 0 1) (aset m 1 0) (aset m 2 0) (aset m 3 1) (aset m 4 0) (aset m 5 0)
  m)

(defn mul!
  "dest := m · n. Reads both fully before writing, so dest may alias either."
  [^js dest ^js m ^js n]
  (let [a (+ (* (aget m 0) (aget n 0)) (* (aget m 2) (aget n 1)))
        b (+ (* (aget m 1) (aget n 0)) (* (aget m 3) (aget n 1)))
        c (+ (* (aget m 0) (aget n 2)) (* (aget m 2) (aget n 3)))
        d (+ (* (aget m 1) (aget n 2)) (* (aget m 3) (aget n 3)))
        e (+ (* (aget m 0) (aget n 4)) (* (aget m 2) (aget n 5)) (aget m 4))
        f (+ (* (aget m 1) (aget n 4)) (* (aget m 3) (aget n 5)) (aget m 5))]
    (aset dest 0 a) (aset dest 1 b) (aset dest 2 c)
    (aset dest 3 d) (aset dest 4 e) (aset dest 5 f)
    dest))

(defn local!
  "dest := T(pos) · T(piv) · R(rot) · K(skew) · S(scale) · T(-piv)

  The transform CONJUGATED BY ITS PIVOT, which is to say: do the rotation, skew
  and scale in a frame shifted to `piv`, so the point `piv` of the node's own
  coordinates does not move however they change. Toon Boom's layer pivot, Flash's
  transformation point, After Effects' anchor point; `xform-paths` says why it is
  in the decomposition rather than solved for per drag.

  Written out closed-form rather than as six matrix products, because this runs
  per node per frame and each product would allocate. The derivation, so the
  constants are checkable rather than trusted:

      R·K·S  =  | c -s | · | 1 kx | · | sx  0 |
                | s  c |   | ky 1 |   |  0 sy |

      K·S    =  | sx     kx·sy |
                | ky·sx     sy |

      R·K·S  =  | sx(c - s·ky)   sy(c·kx - s) |
                | sx(s + c·ky)   sy(s·kx + c) |

  which is the linear part, UNTOUCHED BY THE PIVOT — a conjugation by a
  translation cannot change it, which is why a pivot is free to move without
  reshaping anything. All of it lands in the translation:

      T(p)·T(a)·M·T(-a)  =  T(p + a - M·a) · M

  so with `a = [0 0]` this is exactly `T(pos)·R·K·S` and a node with no pivot
  composes as it always did, to the bit.

  :skew is stored as shear FACTORS, not angles — kx is x gained per unit y — so
  that the identity is 0 and a decomposition round-trips without a tangent."
  [^js dest pos piv rot scale skew]
  (let [c  (js/Math.cos rot)
        s  (js/Math.sin rot)
        sx (ch/component scale 0)
        sy (ch/component scale 1)
        kx (ch/component skew 0)
        ky (ch/component skew 1)
        ax (ch/component piv 0)
        ay (ch/component piv 1)
        a  (* sx (- c (* s ky)))
        b  (* sx (+ s (* c ky)))
        c* (* sy (- (* c kx) s))
        d  (* sy (+ (* s kx) c))]
    (aset dest 0 a)
    (aset dest 1 b)
    (aset dest 2 c*)
    (aset dest 3 d)
    (aset dest 4 (+ (ch/component pos 0) ax (- (+ (* a ax) (* c* ay)))))
    (aset dest 5 (+ (ch/component pos 1) ay (- (+ (* b ax) (* d ay)))))
    dest))

(defn pinv
  "The parent-inverse, captured at the moment of parenting so the child does not
  jump when it acquires a parent. Blender's `parent_inverse`. Small, and its
  absence is the kind of thing that makes a parenting feature feel broken."
  [n]
  (if-let [p (:pinv n)]
    (js/Float64Array.from (clj->js p))
    nil))

(defn world!
  "dest := parent · pinv · local. `parent` is nil at the root, `pinv-m` nil until
  something is reparented. `scratch` is a 6-element Float64Array the caller owns;
  it is an argument rather than an allocation because this runs per node per
  frame."
  [^js dest ^js parent ^js pinv-m ^js local ^js scratch]
  (cond
    (and parent pinv-m) (mul! dest parent (mul! scratch pinv-m local))
    parent              (mul! dest parent local)
    pinv-m              (mul! dest pinv-m local)
    :else               (doto dest (.set local))))

(defn invert
  "The inverse of a 2x3 affine, or nil when it has none — a node scaled to
  nothing has no inside to draw into."
  [^js m]
  (let [[a b c d e f] (array-seq m)
        det (- (* a d) (* b c))]
    (when-not (zero? det)
      (js/Float64Array. #js [(/ d det) (/ (- b) det) (/ (- c) det) (/ a det)
                             (/ (- (* c f) (* d e)) det) (/ (- (* b e) (* a f)) det)]))))

(defn apply-pt!
  "out[2i], out[2i+1] := m · (x, y)."
  [^js out i ^js m x y]
  (aset out (* 2 i)       (+ (* (aget m 0) x) (* (aget m 2) y) (aget m 4)))
  (aset out (inc (* 2 i)) (+ (* (aget m 1) x) (* (aget m 3) y) (aget m 5)))
  out)

(defn mean-scale
  "The geometric-mean scale of a transform, sqrt|det|.

  A disc under a non-uniform transform is an ellipse and this rasteriser has no
  ellipse — the iris is a disc because at 320x200 it is a few pixels across, and
  a few-pixel ellipse is not a shape, it is a stair. So a disc's radius takes the
  mean scale. For a similarity, which is the only transform the anchor fit
  produces, this is exact."
  [^js m]
  (js/Math.sqrt (js/Math.abs (- (* (aget m 0) (aget m 3))
                                (* (aget m 1) (aget m 2))))))

;; ---------------------------------------------------------------------------

(defn problems
  "Human-readable reasons this map is not a usable node. Empty means it is one.

  Worth having at all because app-db holds only authored data, which is what
  makes validating every event affordable; this is the per-node half of that."
  [n]
  (let [k     (:kind n)
        valid (get valid-paths k)]
    (-> []
        (cond->
         (nil? (:id n))               (conj "no :id")
         (not (contains? kinds k))    (conj (str ":kind " (pr-str k) " is not one of " (pr-str kinds)))
         (and (contains? kinds k)
              (not (contains? implemented-kinds k)))
         (conj (str ":kind " k " is in the vocabulary but not implemented"))

         (and (= k :instance) (not (keyword? (source n))))
         (conj "an instance needs :source {:symbol <symbol-id>}")
         (and (= k :instance)
              (let [{:keys [in speed end]} (playback-of n)]
                (not (and (finite-number? in) (<= 0 in)
                          (finite-number? speed) (<= 0 speed)
                          (#{:stop :hold :loop} end)))))
         (conj "playback needs a nonnegative finite :in and :speed, and :end :stop, :hold or :loop")
         (:layout n)
         (conj ":layout is not a node field — a lane is how the timeline DRAWS a symbol, not a thing in the document")
         (and (= k :audio) (not (some (:source n) [:footage :sound])))
         (conj "an audio node needs a :source :footage or :sound")
         (and (some? (get-in n [:time :rate]))
              (not (and (finite-number? (get-in n [:time :rate]))
                        (pos? (get-in n [:time :rate])))))
         (conj ":time :rate must be positive")
         (contains? (:channels n) [:xform :anchor])
         (conj (str ":anchor is now [:xform :pivot], and it means the same point "
                    "in the same coordinates — but the composition around it "
                    "changed from T(pos)·M·T(-a) to T(pos)·T(a)·M·T(-a), so a "
                    "turned or scaled node reading one as the other would move"))
         (nil? (:z n))                (conj "no :z — draw order is authored per scene, not implied by the tree")
         (and (:span n) (not (and (vector? (:span n)) (= 2 (count (:span n)))
                                    (every? finite-number? (:span n))
                                    (apply < (:span n)))))
         (conj ":span must be a finite, increasing [in out]")
         (some? (get-in n [:time :in]))
         (conj ":time has an :in — an instance's first frame is the start of its own :span")
         (let [hs (get-in n [:time :holds])]
           (and (some? hs) (not (and (vector? hs) (every? finite-number? hs)
                                     (or (empty? hs) (apply < hs))))))
         (conj ":time :holds must be a vector of increasing frames"))

        ;; `[:xform :anchor]` is excluded because it has a NAMED refusal above.
        ;; It is the one invalid path a stored document is likely to carry — every
        ;; schema-6 drawing and placement had one, and schema 7 refused them all
        ;; rather than convert — so "not valid on a :poly node" would be the first
        ;; thing a person saw, and it says nothing about what to do. The precedent
        ;; is `symbol/problems`' refusal of `:trace`.
        (into (when valid
                (for [[path _] (:channels n)
                      :when    (and (not (contains? valid path))
                                    (not= path [:xform :anchor]))]
                  (str "channel " (pr-str path) " is not valid on a " k " node"))))

        (into (for [[path c] (:channels n)
                    p        (ch/problems c)]
                (str "channel " (pr-str path) ": " p))))))
